import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { FilterUtils, PaginationUtils } from '@ocean/utils'
import { Card } from 'types/card'
import { createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { getRapidElearning } from 'modules/trainingLibrary/RapidElearning/api/rapidElearningService'
import {
  RapidElearningFilterItem,
  RapidElearningFilterItems,
} from 'modules/trainingLibrary/RapidElearning/types'
import { RapidElearningState, ApiRapidElearningResponse } from './types'

const initialState: RapidElearningState = {
  rapidElearningData: {},
  rapidElearningFilters: {
    isCompleted: { keyType: FilterKeyType.Boolean, searchKey: 'result.isCompleted' },
    categoryId: { searchKey: 'typeSpecificValues.categoryId' },
  },
}

const rapidElearningSlice = createSlice({
  name: 'rapidElearning',
  initialState,
  reducers: {
    rapidElearningDataLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiRapidElearningResponse>>
    ) {
      state.rapidElearningData = action.payload
    },
    setRapidElearningFilters(state, action: PayloadAction<RapidElearningFilterItem>) {
      const filter = state.rapidElearningFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { rapidElearningDataLoaded } = rapidElearningSlice.actions
export const { setRapidElearningFilters } = rapidElearningSlice.actions
export default rapidElearningSlice.reducer

export const getFilteredRapidElearning = createSelector(
  (rapidElearningCards: Card[] = [], filters: RapidElearningFilterItems) => ({
    rapidElearningCards,
    filters,
  }),
  ({ rapidElearningCards, filters }) => FilterUtils.filterData(filters, rapidElearningCards)
)

export const getRapidElearningPageItems = createSelector(
  (
    rapidElearningCards: Card[] = [],
    filters: RapidElearningFilterItems,
    currentPageId: number,
    currentPageSize: number
  ) => ({
    filteredRapidElearningCards: getFilteredRapidElearning(rapidElearningCards, filters),
    currentPageId,
    currentPageSize,
  }),
  ({ filteredRapidElearningCards, currentPageId, currentPageSize }) =>
    PaginationUtils.getPageItems<Card>(filteredRapidElearningCards, currentPageId, currentPageSize)
)

export const loadRapidElearningData = () => {
  return createApiAction({
    apiCalls: {
      rapidElearning: getRapidElearning,
    },
    payloadAction: rapidElearningDataLoaded,
  })
}
